import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { usersAPI, projectsAPI, invoicesAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  Plus, 
  Trash2, 
  Users, 
  User,
  Shield,
  Copy,
  Eye,
  EyeOff,
  FolderKanban,
  X,
  Check
} from 'lucide-react';
import { toast } from 'sonner';

const UsersPage = () => {
  const { isSuperAdmin, user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  
  // Form state
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [name, setName] = useState('');
  const [role, setRole] = useState('pilot');
  const [projectId, setProjectId] = useState('');
  const [selectedProjectIds, setSelectedProjectIds] = useState([]);

  const generatePassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789!@#$%';
    let password = '';
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setPassword(password);
    setShowPassword(true);
  };

  const fetchData = async () => {
    try {
      const [usersRes, projectsRes] = await Promise.all([
        usersAPI.list(),
        isSuperAdmin ? projectsAPI.list() : Promise.resolve({ data: [] })
      ]);
      setUsers(usersRes.data);
      setProjects(projectsRes.data);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [isSuperAdmin]);

  const resetForm = () => {
    setUsername('');
    setPassword('');
    setShowPassword(false);
    setName('');
    setRole('pilot');
    setProjectId(currentUser?.project_id || '');
    setSelectedProjectIds([]);
  };

  const toggleProjectSelection = (projId) => {
    if (selectedProjectIds.includes(projId)) {
      setSelectedProjectIds(selectedProjectIds.filter(id => id !== projId));
    } else {
      if (selectedProjectIds.length >= 10) {
        toast.error('Admin pode gerenciar no máximo 10 projetos');
        return;
      }
      setSelectedProjectIds([...selectedProjectIds, projId]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const userData = {
      username,
      password,
      name,
      role,
      project_id: role === 'pilot' ? projectId : null,
      project_ids: role === 'admin' ? selectedProjectIds : []
    };

    try {
      await usersAPI.create(userData);
      toast.success('Usuário criado com sucesso!');
      setCreateOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar usuário');
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Tem certeza que deseja excluir este usuário?')) return;

    try {
      await usersAPI.delete(userId);
      toast.success('Usuário excluído com sucesso!');
      fetchData();
    } catch (error) {
      toast.error('Erro ao excluir usuário');
    }
  };

  const handleToggleInvoices = async (user) => {
    try {
      const newVal = !user.invoices_enabled;
      await invoicesAPI.setUserAccess(user.id, newVal);
      toast.success(newVal ? 'Prestação de Contas habilitada' : 'Prestação de Contas desabilitada');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao alterar acesso');
    }
  };

  const copyCredentials = () => {
    const text = `Usuário: ${username}\nSenha: ${password}`;
    navigator.clipboard.writeText(text);
    toast.success('Credenciais copiadas!');
  };

  const getProjectName = (id) => {
    const project = projects.find(p => p.id === id);
    return project?.name || 'N/A';
  };

  const getProjectNames = (ids) => {
    if (!ids || ids.length === 0) return 'N/A';
    return ids.map(id => getProjectName(id)).join(', ');
  };

  const getRoleBadge = (role) => {
    switch (role) {
      case 'super_admin': return { label: 'SUPER ADMIN', className: 'badge-warning' };
      case 'admin': return { label: 'ADMIN', className: 'badge-info' };
      case 'pilot': return { label: 'PILOTO', className: 'badge-active' };
      default: return { label: role.toUpperCase(), className: 'badge-status' };
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="users-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-rajdhani font-semibold text-navy uppercase tracking-wider flex items-center gap-2">
          <Users size={20} className="text-gold" />
          GESTÃO DE USUÁRIOS
        </h2>
        
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button className="btn-primary" onClick={() => { resetForm(); setCreateOpen(true); }} data-testid="create-user-btn">
              <Plus size={18} className="mr-2" />
              NOVO USUÁRIO
            </Button>
          </DialogTrigger>
          
          <DialogContent className="bg-white border border-gray-200 max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-gold font-rajdhani uppercase tracking-wider">
                CRIAR NOVO USUÁRIO
              </DialogTitle>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-6 mt-4">
              <div className="space-y-2">
                <Label className="text-gray-500 font-mono text-xs uppercase">Nome Completo</Label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="input-hud"
                  placeholder="Ex: João Silva"
                  required
                  data-testid="user-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-gray-500 font-mono text-xs uppercase">Nome de Usuário</Label>
                <Input
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="input-hud"
                  placeholder="Ex: joao.silva"
                  required
                  data-testid="user-username-input"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-gray-500 font-mono text-xs uppercase">Senha</Label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="input-hud pr-10"
                      placeholder="Senha segura"
                      required
                      data-testid="user-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gold"
                    >
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  <Button type="button" onClick={generatePassword} className="btn-secondary">
                    GERAR
                  </Button>
                </div>
                {password && (
                  <Button 
                    type="button" 
                    onClick={copyCredentials} 
                    className="w-full mt-2 btn-secondary text-xs"
                  >
                    <Copy size={14} className="mr-2" />
                    COPIAR CREDENCIAIS
                  </Button>
                )}
              </div>

              <div className="space-y-2">
                <Label className="text-gray-500 font-mono text-xs uppercase">Função</Label>
                <Select value={role} onValueChange={(v) => { setRole(v); setSelectedProjectIds([]); setProjectId(''); }}>
                  <SelectTrigger className="input-hud" data-testid="user-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white border border-gray-200">
                    {isSuperAdmin && (
                      <SelectItem value="admin" className="text-navy hover:bg-gold/10">
                        Administrador de Projeto
                      </SelectItem>
                    )}
                    <SelectItem value="pilot" className="text-navy hover:bg-gold/10">
                      Piloto
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Para Admin - Múltiplos Projetos */}
              {isSuperAdmin && role === 'admin' && (
                <div className="space-y-2">
                  <Label className="text-gray-500 font-mono text-xs uppercase">
                    Projetos (máx. 10) - {selectedProjectIds.length} selecionado(s)
                  </Label>
                  <div className="grid grid-cols-1 gap-2 max-h-48 overflow-y-auto p-2 bg-black/30 rounded-sm border border-gray-200">
                    {projects.map((project) => {
                      const isSelected = selectedProjectIds.includes(project.id);
                      return (
                        <button
                          key={project.id}
                          type="button"
                          onClick={() => toggleProjectSelection(project.id)}
                          className={`flex items-center gap-3 p-3 rounded-sm transition-colors text-left
                                    ${isSelected 
                                      ? 'bg-gold/10 border border-gold/50' 
                                      : 'bg-black/30 border border-gray-200 hover:border-gold/30'}`}
                        >
                          <div className={`w-5 h-5 rounded-sm border flex items-center justify-center
                                        ${isSelected ? 'bg-gold border-gold' : 'border-white/30'}`}>
                            {isSelected && <Check size={14} className="text-black" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className={`font-medium truncate ${isSelected ? 'text-gold' : 'text-navy'}`}>
                              {project.name}
                            </p>
                            {project.description && (
                              <p className="text-gray-500 text-xs truncate">{project.description}</p>
                            )}
                          </div>
                          <FolderKanban size={16} className={isSelected ? 'text-gold' : 'text-gray-500'} />
                        </button>
                      );
                    })}
                  </div>
                  {selectedProjectIds.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {selectedProjectIds.map(id => (
                        <span key={id} className="badge-info flex items-center gap-1 text-xs">
                          {getProjectName(id)}
                          <button type="button" onClick={() => toggleProjectSelection(id)}>
                            <X size={12} />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Para Piloto - Único Projeto */}
              {role === 'pilot' && (
                <div className="space-y-2">
                  <Label className="text-gray-500 font-mono text-xs uppercase">Projeto</Label>
                  <Select value={projectId} onValueChange={setProjectId} required>
                    <SelectTrigger className="input-hud" data-testid="user-project-select">
                      <SelectValue placeholder="Selecione o projeto" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border border-gray-200">
                      {projects.map((project) => (
                        <SelectItem key={project.id} value={project.id} className="text-navy hover:bg-gold/10">
                          {project.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setCreateOpen(false)} className="btn-secondary flex-1">
                  CANCELAR
                </Button>
                <Button type="submit" className="btn-primary flex-1" data-testid="save-user-btn">
                  CRIAR USUÁRIO
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Users Table */}
      <div className="card-hud overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left p-4 text-gray-500 font-mono text-xs uppercase tracking-wider">Usuário</th>
                <th className="text-left p-4 text-gray-500 font-mono text-xs uppercase tracking-wider">Nome</th>
                <th className="text-left p-4 text-gray-500 font-mono text-xs uppercase tracking-wider">Função</th>
                {isSuperAdmin && (
                  <th className="text-left p-4 text-gray-500 font-mono text-xs uppercase tracking-wider">Projeto(s)</th>
                )}
                <th className="text-center p-4 text-gray-500 font-mono text-xs uppercase tracking-wider" title="Prestação de Contas (notas fiscais)">P. CONTAS</th>
                <th className="text-left p-4 text-gray-500 font-mono text-xs uppercase tracking-wider">Criado em</th>
                <th className="text-right p-4 text-gray-500 font-mono text-xs uppercase tracking-wider">Ações</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => {
                const roleBadge = getRoleBadge(user.role);
                const projectDisplay = user.role === 'admin' && user.project_ids?.length > 0
                  ? `${user.project_ids.length} projeto(s)`
                  : getProjectName(user.project_id);
                
                return (
                  <tr key={user.id} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`user-row-${user.id}`}>
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-sm bg-gold/10 flex items-center justify-center">
                          <User size={16} className="text-gold" />
                        </div>
                        <span className="text-navy font-mono text-sm">{user.username}</span>
                      </div>
                    </td>
                    <td className="p-4 text-gray-600">{user.name || '-'}</td>
                    <td className="p-4">
                      <span className={roleBadge.className}>{roleBadge.label}</span>
                    </td>
                    {isSuperAdmin && (
                      <td className="p-4 text-gray-500 text-sm max-w-[200px]">
                        <span className="truncate block" title={user.role === 'admin' ? getProjectNames(user.project_ids) : getProjectName(user.project_id)}>
                          {projectDisplay}
                        </span>
                      </td>
                    )}
                    <td className="p-4 text-center">
                      <button
                        type="button"
                        onClick={() => handleToggleInvoices(user)}
                        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${user.invoices_enabled ? 'bg-gold' : 'bg-gray-300'}`}
                        title="Habilitar Prestação de Contas para este usuário"
                        data-testid={`toggle-invoices-${user.id}`}
                      >
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${user.invoices_enabled ? 'translate-x-4' : 'translate-x-0.5'}`} />
                      </button>
                    </td>
                    <td className="p-4 text-gray-500 text-sm font-mono">
                      {new Date(user.created_at).toLocaleDateString('pt-BR')}
                    </td>
                    <td className="p-4 text-right">
                      {(isSuperAdmin || user.created_by === currentUser?.id) && (
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => handleDelete(user.id)}
                          className="text-gray-500 hover:text-red-500 hover:bg-alert-red/10"
                          data-testid={`delete-user-${user.id}`}
                        >
                          <Trash2 size={16} />
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {users.length === 0 && (
        <div className="card-hud p-8 text-center">
          <Users size={48} className="text-gray-500 mx-auto mb-4" />
          <p className="text-gray-500 font-mono">Nenhum usuário cadastrado</p>
        </div>
      )}
    </div>
  );
};

export default UsersPage;
